import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import type { FormStatus } from '@/types';

const schema = z.object({
  name: z.string().min(2, 'validation.nameMin'),
  email: z.string().email('validation.emailInvalid'),
  subject: z.string().min(4, 'validation.subjectMin'),
  message: z.string().min(10, 'validation.messageMin'),
});

type SchemaType = z.infer<typeof schema>;

export default function ContactSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<FormStatus>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (_data: SchemaType) => {
    setStatus('sending');
    try {
      /* Reemplazar con tus credenciales reales de EmailJS:
         YOUR_SERVICE_ID, YOUR_TEMPLATE_ID, YOUR_PUBLIC_KEY */
      await emailjs.sendForm(
        'YOUR_SERVICE_ID',        // ← Reemplazar
        'YOUR_TEMPLATE_ID',       // ← Reemplazar
        '#contact-form',
        'YOUR_PUBLIC_KEY'         // ← Reemplazar
      );
      setStatus('success');
      reset();
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          {/* Form */}
          <div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-coffee-900 mb-3">
              {t('contact.title')}
            </h2>
            <p className="text-base md:text-lg text-coffee-700 mb-8">{t('contact.subtitle')}</p>

            <form
              id="contact-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-5"
            >
              <div>
                <input
                  {...register('name')}
                  placeholder={t('contact.form.name')}
                  className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-3 text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40 transition-all"
                />
                {errors.name && (
                  <p className="text-terracotta-500 text-sm mt-1">{t(errors.name.message as string)}</p>
                )}
              </div>

              <div>
                <input
                  {...register('email')}
                  placeholder={t('contact.form.email')}
                  className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-3 text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40 transition-all"
                />
                {errors.email && (
                  <p className="text-terracotta-500 text-sm mt-1">{t(errors.email.message as string)}</p>
                )}
              </div>

              <div>
                <input
                  {...register('subject')}
                  placeholder={t('contact.form.subject')}
                  className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-3 text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40 transition-all"
                />
                {errors.subject && (
                  <p className="text-terracotta-500 text-sm mt-1">{t(errors.subject.message as string)}</p>
                )}
              </div>

              <div>
                <textarea
                  {...register('message')}
                  rows={4}
                  placeholder={t('contact.form.message')}
                  className="w-full bg-coffee-50 border border-coffee-200 rounded-xl px-4 py-3 text-coffee-900 placeholder:text-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-400/40 transition-all resize-none"
                />
                {errors.message && (
                  <p className="text-terracotta-500 text-sm mt-1">{t(errors.message.message as string)}</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Button
                  variant="primary"
                  size="md"
                  type="submit"
                  disabled={status === 'sending'}
                  leftIcon={<Send className="w-4 h-4" />}
                >
                  {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
                </Button>

                <div className="flex items-center gap-2 text-coffee-700">
                  <Phone className="w-5 h-5 text-coffee-400" />
                  <span className="text-sm">{t('contact.phone')}</span>
                </div>
              </div>

              {status === 'success' && (
                <p className="text-green-600 text-sm font-medium">{t('contact.form.success')}</p>
              )}
              {status === 'error' && (
                <p className="text-terracotta-500 text-sm font-medium">{t('contact.form.error')}</p>
              )}
            </form>
          </div>

          {/* Floating Image */}
          <div className="hidden lg:flex items-center justify-center">
            <motion.img
              src="/images/coffee-png.png"
              alt="Café decorativo"
              className="max-h-[28rem] drop-shadow-2xl"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
